import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild} from '@angular/core';
import {deepClone, ScreenService} from '@app/core';

const PARALLEL_TOGGLE_TOOLTIP = 'Búsqueda por términos: cada palabra se busca por separado (OR). Escribe y presiona espacio, o pega desde Excel (filas/columnas) para añadir términos.';

/** Delimitadores para partir texto pegado desde Excel: saltos de línea, tabulaciones, comas, punto y coma */
const PASTE_DELIMITERS = /[\r\n\t,;]+/;

@Component({
  selector: 'app-pocket-searchbar',
  standalone: false,

  templateUrl: './pocket-searchbar.component.html',
  styleUrl: './pocket-searchbar.component.less'
})
export class PocketSearchbarComponent implements OnChanges, AfterViewInit {

  @Input() searchValue: string = '';
  @Output() searchValueChange = new EventEmitter<string>();

  @Output() searchEvent = new EventEmitter<void>();

  @Input() showParallelToggle = false;
  @Input() parallelSearchMode = false;
  /** Cuando true, el searchbar está siempre expandido (sin botón colapsar) */
  @Input() alwaysExpanded = false;
  /** Cuando true, el input ocupa todo el ancho disponible (p. ej. en drawers) */
  @Input() fullWidth = false;
  /** Cuando true, oculta iconos (lupa, limpiar) y reduce padding izquierdo (para drawers). El toggle modo paralelo se sigue mostrando. */
  @Input() hideIcons = false;
  /** Cuando true, el searchbar usa el doble de ancho (300px en lugar de 150px). Para Administración y tabla SO. */
  @Input() wide = false;
  @Input() placeholder = 'Buscar...';
  @Output() parallelSearchModeChange = new EventEmitter<boolean>();
  @Output() searchTermsChange = new EventEmitter<string[]>();

  readonly parallelToggleTooltip = PARALLEL_TOGGLE_TOOLTIP;

  /** Términos confirmados (badges) en modo paralelo */
  activeTerms: string[] = [];
  /** Texto actual en el input cuando está en modo paralelo (palabra en edición) */
  currentInputValue = '';

  private _hideInput: boolean = false;
  private _isInitializing: boolean = true;
  
  @Input()
  set hideInput(value: boolean) {
    // Solo limpiar si hideInput cambia de false a true Y el valor no está vacío Y no estamos en la inicialización
    // Esto evita limpiar el valor cuando el componente se renderiza con un valor guardado
    if (value && !this._hideInput && this.searchValue && !this._isInitializing) {
      this.clear();
    }
    this._hideInput = value;
    // Marcar que la inicialización ha terminado después del primer cambio
    if (this._isInitializing) {
      this._isInitializing = false;
    }
  }
  
  get hideInput(): boolean {
    return this._hideInput;
  }

  @Output() showInputEvent = new EventEmitter<true>();

  @ViewChild('searchInput') searchInput!: ElementRef;

  showInput: boolean = false;
  private isInitialized: boolean = false;
  shouldDisableTransition: boolean = false; // Se establecerá correctamente en ngOnChanges

  isJustClear: boolean = false;

  constructor(
    public screenService: ScreenService
  ) {
  }

  ngAfterViewInit(): void {
    // Si el searchValue está vacío al inicializar, mantener la transición deshabilitada
    // durante la inicialización para evitar la animación de expandirse/encogerse
    // Luego habilitar las transiciones para cambios futuros después de un breve delay
    if (!this.searchValue || !this.searchValue.trim()) {
      // Mantener shouldDisableTransition = true durante la inicialización
      // Luego habilitar transiciones para futuros cambios
      setTimeout(() => {
        this.shouldDisableTransition = false;
      }, 250); // Esperar un poco más que la duración de la transición (0.2s)
    } else {
      // Si hay valor, habilitar transiciones desde el inicio
      this.shouldDisableTransition = false;
    }
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (this.alwaysExpanded || changes['alwaysExpanded']) {
      if (this.alwaysExpanded) {
        this.showInput = true;
        this.isInitialized = true;
        this.shouldDisableTransition = false;
        return;
      }
    }
    // Si searchValue tiene un valor cuando se inicializa, expandir el input automáticamente
    if (changes['searchValue']) {
      const searchValueChange = changes['searchValue'];
      const hasValue = this.parallelSearchMode
        ? (this.activeTerms.length > 0 || !!this.currentInputValue?.trim())
        : (this.searchValue && this.searchValue.trim());
      if (searchValueChange.firstChange && hasValue) {
        this.showInput = true;
        this.isInitialized = true;
        this.shouldDisableTransition = false;
      } else if (searchValueChange.firstChange) {
        this.showInput = false;
        this.isInitialized = true;
        this.shouldDisableTransition = true;
      }
    }
    if (changes['parallelSearchMode'] && !changes['parallelSearchMode'].firstChange) {
      if (this.parallelSearchMode) {
        this.syncFromSearchValueToParallel();
      }
    }
  }

  private syncFromSearchValueToParallel(): void {
    if (!this.parallelSearchMode || !this.searchValue?.trim()) return;
    this.activeTerms = this.searchValue.trim().split(/\s+/).filter(Boolean);
    this.currentInputValue = '';
    this.searchTermsChange.emit([...this.activeTerms]);
  }

  focusSearchInput(): void {
    setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
  }

  toggleParallelMode(): void {
    const wasParallel = this.parallelSearchMode;
    this.parallelSearchMode = !this.parallelSearchMode;
    this.parallelSearchModeChange.emit(this.parallelSearchMode);
    if (this.parallelSearchMode) {
      this.syncFromSearchValueToParallel();
    } else if (wasParallel) {
      this.searchValue = [...this.activeTerms, this.currentInputValue.trim()].filter(Boolean).join(' ');
      this.searchValueChange.emit(this.searchValue);
      this.activeTerms = [];
      this.currentInputValue = '';
    }
  }

  onInputChangeParallel(newVal: string): void {
    const trimmed = newVal.trimEnd();
    if (newVal.endsWith(' ') && trimmed) {
      this.addTermsAndEmit([trimmed]);
    } else {
      this.currentInputValue = newVal;
    }
  }

  /**
   * Maneja el pegado en modo paralelo: divide el texto por saltos de línea, tabulaciones, comas o punto y coma
   * (típico de Excel) y añade cada parte como badge, sin duplicados.
   */
  onPasteParallel(event: ClipboardEvent): void {
    if (!this.parallelSearchMode) return;
    const pasted = event.clipboardData?.getData('text');
    if (!pasted?.trim()) return;
    event.preventDefault();
    const terms = pasted
      .split(PASTE_DELIMITERS)
      .map(s => s.trim())
      .filter(Boolean);
    if (terms.length === 0) return;
    this.addTermsAndEmit(terms);
  }

  private addTermsAndEmit(newTerms: string[]): void {
    const existing = new Set(this.activeTerms.map(t => t.toLowerCase()));
    const toAdd = newTerms.filter(t => !existing.has(t.toLowerCase()));
    toAdd.forEach(t => existing.add(t.toLowerCase()));
    this.activeTerms = [...this.activeTerms, ...toAdd];
    this.currentInputValue = '';
    this.searchTermsChange.emit([...this.activeTerms]);
    this.searchValueChange.emit(this.getEffectiveSearchValue());
    this.searchEvent.emit();
  }

  removeTerm(index: number): void {
    this.activeTerms = this.activeTerms.filter((_, i) => i !== index);
    this.searchTermsChange.emit([...this.activeTerms]);
    this.searchValueChange.emit(this.getEffectiveSearchValue());
    this.searchEvent.emit();
  }

  private getEffectiveSearchValue(): string {
    return this.parallelSearchMode
      ? [...this.activeTerms, this.currentInputValue.trim()].filter(Boolean).join(' ')
      : this.searchValue;
  }

  search(value: string = this.searchValue) {
    if (this.parallelSearchMode) {
      const trimmed = (value || '').trim();
      if (trimmed) {
        this.addTermsAndEmit([trimmed]);
      }
      this.searchValueChange.emit(this.getEffectiveSearchValue());
      this.searchEvent.emit();
    } else {
      this.searchValue = value;
      this.searchValueChange.emit(this.searchValue);
      this.searchEvent.emit();
    }
  }

  clear() {
    if (this.parallelSearchMode) {
      this.activeTerms = [];
      this.currentInputValue = '';
      this.searchTermsChange.emit([]);
    }
    this.searchValue = '';
    this.searchValueChange.emit(this.searchValue);
    this.search();
  }

  clearAndCollapse() {
    this.activeTerms = [];
    this.currentInputValue = '';
    this.searchValue = '';
    this.searchValueChange.emit(this.searchValue);
    this.showInput = false;
    this.search();
  }

  toggleShowInput() {
    if (this.alwaysExpanded) return;
    this.showInput = !this.showInput;
    if (!this.showInput) {
      this.clearAndCollapse();
    } else {
      this.showInputEvent.emit(true);
      // Enfoca el input después de que la vista se actualice
      setTimeout(() => {
        if (this.searchInput?.nativeElement) {
          this.searchInput.nativeElement.focus();
        }
      }, 0);
    }
  }

  onInputBlur() {
    if (this.alwaysExpanded) {
      if (this.isJustClear) {
        this.clear();
        setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
      }
      this.isJustClear = false;
      return;
    }
    const value = this.parallelSearchMode
      ? (this.currentInputValue || this.activeTerms.length > 0)
      : deepClone(this.searchValue);
    setTimeout(() => {
      if (!this.isJustClear) {
        if (!value) this.showInput = false;
      } else {
        this.clear();
        this.searchInput?.nativeElement?.focus();
      }
      this.isJustClear = false;
    }, 100);
  }

  onEscapeKey() {
    // Al pulsar Esc, limpiar el contenido y quitar el foco del input
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.blur();
    }
  }

  hasAnySearchValue(): boolean {
    if (this.parallelSearchMode) {
      return this.activeTerms.length > 0 || !!this.currentInputValue?.trim();
    }
    return !!(this.searchValue && this.searchValue.trim());
  }

}
